import { useEffect, useRef } from "react";
import type IJitsiMeetExternalApi from "@jitsi/react-sdk/lib/types/IJitsiMeetExternalApi";
import { useTranslation } from "react-i18next";
import { toast } from "@/hooks/useToast";

/**
 * Jitsi external-API `recordingStatusChanged` payload. Only `on` is load-bearing
 * here; every field is optional and read defensively (the deployed bundle may
 * omit fields, or not emit the event at all).
 */
interface RecordingStatusChangedEvent {
  on?: boolean;
  mode?: string;
  error?: string;
}

/**
 * Shows a ONE-TIME "this meeting is being recorded" notice when Jitsi reports
 * recording has turned ON, via the external-API `recordingStatusChanged` event
 * (camelCase — the same public-name convention the sibling moderation listeners
 * in `useModerationEvents` use and which is verified against the deployed
 * bundle).
 *
 * Jitsi already renders its own native recording indicator; this is the ADDITIVE
 * Orion notice, NOT a blocking consent gate (v1 = notice only, per the locked
 * decision). If the deployed bundle never emits `recordingStatusChanged`, this
 * simply never fires (graceful no-op) — we never fabricate a notice.
 */
export default function useRecordingConsentToast(
  apiRef: IJitsiMeetExternalApi | null,
): void {
  const { t } = useTranslation();
  // Once-per-call guard. Reset ONLY when `apiRef` changes (a new call), never on
  // `t` identity / language changes — otherwise a mid-call language switch would
  // re-run the effect, clear the guard, and re-toast on the next ON event.
  const notifiedRef = useRef(false);
  // Read `t` through a ref so the handler always uses the latest translation
  // without `t` being an effect dependency (which would re-subscribe + reset).
  const tRef = useRef(t);
  tRef.current = t;

  useEffect(() => {
    if (!apiRef) {
      return;
    }
    notifiedRef.current = false;

    function handleRecordingStatusChanged(
      payload?: RecordingStatusChangedEvent,
    ) {
      // Only notify on the OFF→ON transition, and only once per call.
      if (payload?.on !== true || notifiedRef.current) {
        return;
      }
      notifiedRef.current = true;
      toast({
        duration: 6000,
        title: tRef.current("videoCall.recording.consentTitle"),
        description: tRef.current("videoCall.recording.consentDescription"),
      });
    }

    apiRef.on("recordingStatusChanged", handleRecordingStatusChanged);
    return () => {
      apiRef.removeListener(
        "recordingStatusChanged",
        handleRecordingStatusChanged,
      );
    };
  }, [apiRef]);
}
